import { useCallback, useEffect, useRef, useState } from "react";

/**
 * useVoice — thin React wrapper around the browser's Web Speech API.
 *
 * Returns:
 *   supported          – boolean. true if both STT + TTS are available.
 *   listening          – boolean. currently capturing audio.
 *   speaking           – boolean. currently playing TTS audio.
 *   transcript         – string. interim + final transcript text.
 *   startListening(lang)
 *   stopListening()
 *   speak(text, lang)
 *   stopSpeaking()
 *
 * Notes:
 * - We use continuous=false so the recogniser stops on a natural pause.
 * - The caller is expected to clear the transcript when the conversation turn ends.
 * - Voices are loaded asynchronously; we pick the best matching voice for `lang`
 *   each time speak() is called.
 */
export function useVoice({ onFinal } = {}) {
  const [listening, setListening] = useState(false);
  const [speaking, setSpeaking] = useState(false);
  const [transcript, setTranscript] = useState("");
  const recognitionRef = useRef(null);
  const onFinalRef = useRef(onFinal);

  useEffect(() => {
    onFinalRef.current = onFinal;
  }, [onFinal]);

  const SpeechRecognition =
    typeof window !== "undefined" &&
    (window.SpeechRecognition || window.webkitSpeechRecognition);

  const supported =
    !!SpeechRecognition &&
    typeof window !== "undefined" &&
    "speechSynthesis" in window;

  const startListening = useCallback(
    (lang = "en-IN") => {
      if (!SpeechRecognition) return;
      // Stop any ongoing TTS so the user isn't talking over the AI
      try {
        window.speechSynthesis?.cancel();
      } catch {
        /* noop */
      }
      try {
        const recog = new SpeechRecognition();
        recog.lang = lang;
        recog.continuous = false;
        recog.interimResults = true;
        recog.maxAlternatives = 1;
        let finalText = "";
        recog.onresult = (event) => {
          let interim = "";
          for (let i = event.resultIndex; i < event.results.length; i++) {
            const t = event.results[i][0].transcript;
            if (event.results[i].isFinal) finalText += t;
            else interim += t;
          }
          setTranscript((finalText + interim).trim());
        };
        recog.onerror = () => {
          setListening(false);
        };
        recog.onend = () => {
          setListening(false);
          const text = finalText.trim();
          if (text) {
            onFinalRef.current?.(text);
          }
        };
        recognitionRef.current = recog;
        setTranscript("");
        setListening(true);
        recog.start();
      } catch {
        setListening(false);
      }
    },
    [SpeechRecognition],
  );

  const stopListening = useCallback(() => {
    try {
      recognitionRef.current?.stop();
    } catch {
      /* noop */
    }
    setListening(false);
  }, []);

  const speak = useCallback((text, lang = "en-IN") => {
    if (!text || typeof window === "undefined" || !("speechSynthesis" in window))
      return;
    try {
      const synth = window.speechSynthesis;
      synth.cancel(); // flush any queued utterance
      const utter = new SpeechSynthesisUtterance(text);
      utter.lang = lang;
      utter.rate = 1.02;
      utter.pitch = 1.0;
      utter.volume = 1.0;

      const pickVoice = () => {
        const voices = synth.getVoices() || [];
        if (!voices.length) return null;
        // Prefer exact lang match, then language prefix, then any English fallback.
        const exact = voices.find((v) => v.lang === lang);
        if (exact) return exact;
        const prefix = lang.split("-")[0];
        const partial = voices.find((v) => v.lang?.startsWith(prefix));
        if (partial) return partial;
        return voices.find((v) => v.lang?.startsWith("en")) || voices[0];
      };
      const v = pickVoice();
      if (v) utter.voice = v;
      utter.onstart = () => setSpeaking(true);
      utter.onend = () => setSpeaking(false);
      utter.onerror = () => setSpeaking(false);
      synth.speak(utter);
    } catch {
      setSpeaking(false);
    }
  }, []);

  const stopSpeaking = useCallback(() => {
    try {
      window.speechSynthesis?.cancel();
    } catch {
      /* noop */
    }
    setSpeaking(false);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      try {
        recognitionRef.current?.stop();
      } catch {
        /* noop */
      }
      try {
        window.speechSynthesis?.cancel();
      } catch {
        /* noop */
      }
    };
  }, []);

  return {
    supported,
    listening,
    speaking,
    transcript,
    startListening,
    stopListening,
    speak,
    stopSpeaking,
    setTranscript,
  };
}
